/**
 * CandidatesTab — the review inbox and the reader pane: filter the candidates, pick one, decide. Demo page: /email
 * (inbox list beside a reading pane, a toolbar of scoped selects above).
 */
import { LibraryBig, X } from 'lucide-react';
import { Button, Card, EmptyState, QueryBoundary, SkeletonList, Toolbar } from '../../../components/ui/index.js';
import { Card as PanelCard } from '../../../components/tailgrids/core/card.js';
import { FilterSelect, sentenceCase } from '../../../components/ui/FilterSelect.js';
import { CandidateDecisionWorkspace } from './CandidateDecisionWorkspace.js';
import { CandidateRow } from './CandidateRow.js';
import { STATUSES, TARGET_TYPE_LABELS, typeLabel } from './catalog-helpers.js';
import type { useCatalogReview } from './useCatalogReview.js';

type CatalogReview = ReturnType<typeof useCatalogReview>;

export function CandidatesTab({ review }: { review: CatalogReview }) {
  const { queries, filters, actions, busy, selectedId } = review;
  const candidates = queries.candidates.data?.data ?? [];
  const selected = queries.selected.data?.data ?? null;
  const statusLabel = filters.activeStatuses.map(sentenceCase).join(' + ');
  const typeFilterLabel = filters.activeTargetTypes.map(typeLabel).join(' + ');

  return (
    <>
      <PanelCard>
        <Toolbar
          className="mb-0"
          title={`${candidates.length} ${candidates.length === 1 ? 'candidate' : 'candidates'}`}
          right={
            <>
              <FilterSelect
                label="Status"
                anyLabel="All statuses"
                value={filters.status}
                options={STATUSES.map((value) => ({ value, label: sentenceCase(value) }))}
                onChange={filters.setStatus}
              />
              <FilterSelect
                label="Type"
                anyLabel="All types"
                value={filters.targetType}
                options={filters.targetTypes.map((value) => ({
                  value,
                  label: TARGET_TYPE_LABELS[value] ?? sentenceCase(value),
                }))}
                onChange={filters.setTargetType}
              />
              {filters.activeStatuses.length > 1 && (
                <Button size="sm" variant="ghost" onClick={filters.clearStatuses}>
                  Status: {statusLabel} <X size={13} aria-hidden="true" />
                </Button>
              )}
              {filters.activeTargetTypes.length > 1 && (
                <Button size="sm" variant="ghost" onClick={filters.clearTargetTypes}>
                  Type: {typeFilterLabel} <X size={13} aria-hidden="true" />
                </Button>
              )}
            </>
          }
        />
      </PanelCard>

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[380px_minmax(0,1fr)]">
        <Card title="Candidate inbox">
          <p className="-mt-3 mb-4 text-xs text-muted">Select a candidate to review its value and daemon findings.</p>
          <div className="-mx-5 -mb-5 max-h-[720px] divide-y overflow-auto border-t">
            <QueryBoundary query={queries.candidates} label="candidates" loading={<SkeletonList count={4} />}>
              {() =>
                candidates.length === 0 ? (
                  <EmptyState
                    icon={LibraryBig}
                    title="No candidates"
                    description="No catalog candidates match the current filters."
                  />
                ) : (
                  candidates.map((candidate) => (
                    <CandidateRow
                      key={candidate.id}
                      candidate={candidate}
                      selected={candidate.id === selectedId}
                      onOpen={() => review.selectCandidate(candidate.id)}
                    />
                  ))
                )
              }
            </QueryBoundary>
          </div>
        </Card>

        <CandidateDecisionWorkspace
          candidate={selected}
          loading={queries.selected.isLoading}
          error={queries.selected.isError}
          onPromote={() => selected && actions.promote(selected.id)}
          onRetryReview={() => selected && actions.retryReview(selected.id)}
          onReject={() => selected && actions.reject(selected.id)}
          onDefer={() => selected && actions.defer(selected.id)}
          busy={busy.candidate}
        />
      </div>
    </>
  );
}
